import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  ensureWpConnection, 
  createWpInstance,
  fetchWpInstanceStatus, 
  reconnectWpInstance,
  logoutWpInstance 
} from '../../store/slices/wpConfigSlice';
import { Button, Alert, Spin, Space, message } from 'antd';
import QRCode from 'react-qr-code';

const PANEL_URL = 'https://panel.sporokulutakip.com';

const StatusDot = ({ color }) => (
  <span style={{
    display: 'inline-block',
    width: 12,
    height: 12,
    borderRadius: '50%',
    background: color,
    marginRight: 8,
    verticalAlign: 'middle',
    boxShadow: `0 0 4px ${color}`
  }} />
);

const WpPage = () => {
  const dispatch = useDispatch();
  const { status: fetchStatus, instance } = useSelector(state => state.wpConfig);
  const selectedBranch = useSelector(state => state.branches.selectedBranch);
  const intervalRef = useRef();
  const pollingRef = useRef(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  const wpStatus = instance?.status || null;
  const isConnected = wpStatus?.status === 'connected';
  const needsQR = wpStatus?.status === 'qr_pending' || wpStatus?.status === 'initializing';
  const isInstanceNotFound = !instance || instance.instanceExists === false;
  // Loading'i sadece ilk yüklemede göster, polling sırasında gösterme
  const loading = fetchStatus === 'loading' && (isInitialLoad || !pollingRef.current);

  // İlk yükleme: status kontrolü yap - branchId değiştiğinde
  useEffect(() => {
    // Önce mevcut interval'ı temizle
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    pollingRef.current = false;
    setIsInitialLoad(true);

    // Branch seçiliyse status'u yükle
    if (selectedBranch?.id) {
      dispatch(fetchWpInstanceStatus(selectedBranch.id));
    }
  }, [dispatch, selectedBranch?.id]);

  // fetchStatus değiştiğinde isInitialLoad'u güncelle
  useEffect(() => {
    if (fetchStatus === 'succeeded' || fetchStatus === 'failed') {
      setIsInitialLoad(false);
    }
  }, [fetchStatus]);

  // Polling: Bağlı değilse her 10 saniyede bir kontrol et
  useEffect(() => {
    // Branch seçili değilse polling yapma
    if (!selectedBranch?.id) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      pollingRef.current = false;
      return;
    }

    // İlk yükleme tamamlanmadan polling başlatma
    if (isInitialLoad) {
      return;
    }

    // Bağlı değilse polling başlat (sadece bir kez)
    if (!isConnected && !intervalRef.current) {
      pollingRef.current = true;
      
      // 10 saniyede bir kontrol et
      intervalRef.current = setInterval(() => {
        if (selectedBranch?.id) {
          pollingRef.current = true;
          dispatch(fetchWpInstanceStatus(selectedBranch.id)).finally(() => {
            pollingRef.current = false;
          });
        }
      }, 10000); // 10 saniye
    } else if (isConnected && intervalRef.current) {
      // Bağlıysa polling'i durdur
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      pollingRef.current = false;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      pollingRef.current = false;
    };
  }, [dispatch, isConnected, selectedBranch?.id, isInitialLoad]);

  const handleCreateInstance = async () => {
    if (!selectedBranch?.id) return;
    const result = await dispatch(createWpInstance(selectedBranch.id));
    if (result.payload?.success) {
      messageApi.success('Entegrasyon oluşturuldu!');
      if (result.payload?.qr) {
        messageApi.info('QR kodu telefonunuzla okutun');
      }
      // Status'u güncellemek için tekrar çağır
      dispatch(fetchWpInstanceStatus(selectedBranch.id));
    } else {
      messageApi.error('Entegrasyon oluşturulamadı');
    }
  };

  const handleEnsureConnection = async () => {
    if (!selectedBranch?.id) return;
    const result = await dispatch(ensureWpConnection(selectedBranch.id));
    if (result.payload?.isConnected) {
      messageApi.success('WhatsApp bağlı!');
    } else if (result.payload?.qr) {
      messageApi.info('QR kodu telefonunuzla okutun');
    }
  };

  const handleReconnect = async () => {
    if (!selectedBranch?.id) return;
    const result = await dispatch(reconnectWpInstance(selectedBranch.id));
    if (result.payload?.connected) {
      messageApi.success('Yeniden bağlandı!');
      dispatch(fetchWpInstanceStatus(selectedBranch.id));
    } else if (result.payload?.needsNewQR) {
      messageApi.warning('Yeni QR gerekiyor');
      handleEnsureConnection();
    }
  };

  const handleLogout = async () => {
    if (!selectedBranch?.id) return;
    const result = await dispatch(logoutWpInstance(selectedBranch.id));
    if (result.payload?.success) {
      messageApi.success('Çıkış yapıldı');
      dispatch(fetchWpInstanceStatus(selectedBranch.id));
    }
  };

  const handleRefresh = () => {
    if (selectedBranch?.id) {
      pollingRef.current = false;
      dispatch(fetchWpInstanceStatus(selectedBranch.id));
    }
  };

  return (
    <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      {contextHolder}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <Spin size="large" tip="Yükleniyor..." />
        </div>
      )}
      {!loading && (
        <div style={{ width: 320, margin: '40px auto 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          
          {/* Branch seçili değil */}
          {!selectedBranch?.id && (
            <Alert 
              type="info" 
              showIcon 
              message="Lütfen bir branş seçiniz" 
              description="WhatsApp entegrasyonunu görüntülemek için önce bir branş seçmelisiniz."
              style={{ marginBottom: 24, width: '100%' }} 
            />
          )}

          {/* Instance yok - İlk kurulum */}
          {selectedBranch?.id && isInstanceNotFound && (
            <>
              <Alert 
                type="warning" 
                showIcon 
                message="Henüz bir WhatsApp entegrasyonu yok." 
                style={{ marginBottom: 24, width: '100%' }} 
              />
              <Button type="primary" size="large" onClick={handleCreateInstance}>
                Yeni Entegrasyon Oluştur
              </Button>
            </>
          )}

          {/* Hata durumu */}
          {selectedBranch?.id && !isInstanceNotFound && instance?.success === false && (
            <>
              <Alert 
                type="error" 
                showIcon 
                message={instance.message || 'Bir hata oluştu.'} 
                style={{ marginBottom: 24, width: '100%' }} 
              />
              <Button type="primary" onClick={handleEnsureConnection}>
                Tekrar Dene
              </Button>
            </>
          )}

          {/* Bağlı Değil - QR Bekleniyor */}
          {selectedBranch?.id && needsQR && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
                <StatusDot color="#ff4d4f" />
                <span style={{ fontWeight: 600, fontSize: 18 }}>Bağlı değil</span>
              </div>
              
              {wpStatus.qr ? (
                <>
                  <div style={{ background: '#fff', padding: 24, borderRadius: 16, boxShadow: '0 2px 8px #f0f1f2', marginBottom: 24 }}>
                    <QRCode value={wpStatus.qr} size={200} />
                  </div>
                  <Space>
                    <Button type="primary" size="large" onClick={handleRefresh}>
                      Okuttum
                    </Button>
                    <Button type="default" onClick={handleEnsureConnection}>
                      QR Yenile
                    </Button>
                  </Space>
                </>
              ) : (
                <div style={{ marginBottom: 24 }}>
                  <Spin tip="QR oluşturuluyor..." />
                  <Button type="dashed" size="large" onClick={handleRefresh} style={{ marginTop: 16 }}>
                    Yenile
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Bağlı Durumu */}
          {selectedBranch?.id && isConnected && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
                <StatusDot color="#52c41a" />
                <span style={{ fontWeight: 600, fontSize: 18 }}>Bağlı</span>
              </div>
              <div style={{ 
                background: '#f5f5f5', 
                padding: 24, 
                borderRadius: 16, 
                boxShadow: '0 2px 8px #f0f1f2', 
                marginBottom: 24, 
                opacity: 0.5, 
                pointerEvents: 'none' 
              }}>
                <QRCode value={PANEL_URL} size={200} />
              </div>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button type="default" block onClick={handleReconnect}>
                  Yeniden Bağlan
                </Button>
                <Button type="default" danger block onClick={handleLogout}>
                  Çıkış Yap
                </Button>
              </Space>
            </>
          )}

          {/* Bağlantı koptu - Auth var ama socket yok */}
          {selectedBranch?.id && wpStatus?.status === 'disconnected' && wpStatus?.hasAuth && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
                <StatusDot color="#faad14" />
                <span style={{ fontWeight: 600, fontSize: 18 }}>Bağlantı Koptu</span>
              </div>
              <Alert 
                type="warning" 
                showIcon 
                message="Bağlantı koptu ama auth bilgileri mevcut" 
                description="Yeniden bağlanmayı deneyin"
                style={{ marginBottom: 24, width: '100%' }} 
              />
              <Space>
                <Button type="primary" onClick={handleReconnect}>
                  Yeniden Bağlan
                </Button>
                <Button type="default" danger onClick={handleLogout}>
                  Çıkış Yap
                </Button>
              </Space>
            </>
          )}

          {/* Ready durumu - Auth var, socket yok */}
          {selectedBranch?.id && wpStatus?.status === 'ready' && !wpStatus?.hasSocket && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
                <StatusDot color="#1890ff" />
                <span style={{ fontWeight: 600, fontSize: 18 }}>Hazır</span>
              </div>
              <Alert 
                type="info" 
                showIcon 
                message="Auth mevcut, socket oluşturulmadı" 
                style={{ marginBottom: 24, width: '100%' }} 
              />
              <Button type="primary" onClick={handleReconnect}>
                Socket Oluştur
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default WpPage; 